import React, { useState, useEffect } from 'react';
import { G4sLayout } from '../../components/gestion4sat/G4sLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Building2, Cpu, Wrench, AlertTriangle, Wifi, WifiOff, Package, TrendingUp, DatabaseZap, CheckCircle2, CircleAlert, Search } from 'lucide-react';
import { getDashboard } from '../../lib/gestion4satApi';
import { useNavigate } from 'react-router-dom';

export default function G4sDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const load = async () => {
    try {
      const d = await getDashboard();
      setData(d);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const kpis = data ? [
    { label: 'Empresas Activas', value: data.total_empresas, icon: Building2, color: 'text-emerald-500', click: () => navigate('/gestion4sat/empresas') },
    { label: 'Dispositivos', value: data.total_dispositivos, icon: Cpu, color: 'text-blue-500', click: () => navigate('/gestion4sat/dispositivos') },
    { label: 'Asignados', value: data.dispositivos_activos, icon: TrendingUp, color: 'text-green-500' },
    { label: 'Stock', value: data.dispositivos_stock, icon: Package, color: 'text-amber-500', click: () => navigate('/gestion4sat/dispositivos?stock=1') },
    { label: 'Online', value: data.dispositivos_online, icon: Wifi, color: 'text-green-400' },
    { label: 'Offline', value: data.dispositivos_offline, icon: WifiOff, color: 'text-red-400' },
    { label: 'Servicios', value: data.total_servicios, icon: Wrench, color: 'text-indigo-500', click: () => navigate('/gestion4sat/servicios') },
    { label: 'Alertas', value: data.alertas_pendientes, icon: AlertTriangle, color: data.alertas_pendientes > 0 ? 'text-red-500' : 'text-muted-foreground', click: () => navigate('/gestion4sat/alertas') },
  ] : [];

  return (
    <G4sLayout>
      <div className="space-y-6" data-testid="g4s-dashboard">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">Vista general del módulo de gestión GPS</p>
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {loading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <Card key={i}><CardContent className="p-4"><Skeleton className="h-12 w-full" /></CardContent></Card>
            ))
          ) : (
            kpis.map((kpi, i) => {
              const Icon = kpi.icon;
              return (
                <Card
                  key={i}
                  className={`${kpi.click ? 'cursor-pointer hover:border-emerald-500/40 transition-colors' : ''}`}
                  onClick={kpi.click}
                  data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Icon className={`h-5 w-5 ${kpi.color}`} />
                      <div>
                        <div className="text-2xl font-black tabular-nums">{kpi.value}</div>
                        <div className="text-xs text-muted-foreground">{kpi.label}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Data Completeness Card */}
        {!loading && data?.datos_completitud && (
          <Card data-testid="datos-completitud-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold flex items-center gap-2">
                <DatabaseZap className="h-4 w-4 text-amber-500" />
                Completitud de Datos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                {/* Progress ring */}
                <div className="relative w-20 h-20 shrink-0">
                  <svg className="w-20 h-20 -rotate-90" viewBox="0 0 36 36">
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none" stroke="currentColor" strokeWidth="3" className="text-muted/30"
                    />
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none" strokeWidth="3" strokeDasharray={`${data.datos_completitud.porcentaje}, 100`}
                      className={data.datos_completitud.porcentaje >= 80 ? 'stroke-emerald-500' : data.datos_completitud.porcentaje >= 50 ? 'stroke-amber-500' : 'stroke-red-500'}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-black tabular-nums">{data.datos_completitud.porcentaje}%</span>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex-1 grid grid-cols-3 gap-3 text-center sm:text-left">
                  <div>
                    <div className="flex items-center gap-1 justify-center sm:justify-start">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                      <span className="text-xs text-muted-foreground">Completas</span>
                    </div>
                    <div className="text-xl font-bold tabular-nums text-emerald-600">{data.datos_completitud.empresas_completas}</div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 justify-center sm:justify-start">
                      <CircleAlert className="h-3.5 w-3.5 text-amber-500" />
                      <span className="text-xs text-muted-foreground">Sin razón social</span>
                    </div>
                    <div className="text-xl font-bold tabular-nums text-amber-600">{data.datos_completitud.empresas_sin_razon}</div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 justify-center sm:justify-start">
                      <Search className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">No en SII</span>
                    </div>
                    <div className="text-xl font-bold tabular-nums">{data.datos_completitud.empresas_sii_no_encontrado}</div>
                  </div>
                </div>

                {/* Action */}
                {data.datos_completitud.empresas_sin_razon > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-500/50 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/20 shrink-0"
                    onClick={() => navigate('/gestion4sat/importar')}
                    data-testid="btn-ir-enriquecer"
                  >
                    <DatabaseZap className="h-4 w-4 mr-1" />
                    Enriquecer
                  </Button>
                )}
              </div>
              {data.datos_completitud.ultimo_enriquecimiento?.finished_at && (
                <p className="text-xs text-muted-foreground mt-3 border-t pt-2">
                  Último enriquecimiento: {new Date(data.datos_completitud.ultimo_enriquecimiento.finished_at).toLocaleString('es-CL')}
                  {' · '}{data.datos_completitud.ultimo_enriquecimiento.actualizados} actualizados
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Top Empresas & Servicios Pendientes */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold flex items-center gap-2">
                <Building2 className="h-4 w-4 text-emerald-500" />
                Top Empresas por Dispositivos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-32 w-full" />
              ) : data?.top_empresas?.length > 0 ? (
                <div className="space-y-3">
                  {data.top_empresas.map((emp, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm truncate mr-2">{emp.razon_social || 'Sin nombre'}</span>
                      <Badge variant="secondary" className="font-mono tabular-nums">{emp.count}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-6">
                  Sin datos. Importa clientes y dispositivos desde la sección Importar Datos.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold flex items-center gap-2">
                <Wrench className="h-4 w-4 text-indigo-500" />
                Servicios Pendientes de Facturación
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-32 w-full" />
              ) : (
                <div className="flex flex-col items-center justify-center py-6">
                  <div className="text-4xl font-black text-amber-500 tabular-nums">
                    {data?.servicios_pendientes || 0}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">servicios por facturar</div>
                  {data?.servicios_pendientes > 0 && (
                    <button
                      onClick={() => navigate('/gestion4sat/facturacion')}
                      className="mt-3 text-xs text-emerald-500 hover:underline"
                    >
                      Ir a Pre-facturación
                    </button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </G4sLayout>
  );
}
